import React, {createContext, useState, useEffect, useCallback, useContext} from "react";
import {API_BASE_URL} from "../config";

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);


export const AuthProvider = ({children}) => {

    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    });

    const [messages, setMessages] = useState([]);
    const [hasUnacknowledgedMessages, setHasUnacknowledgedMessages] = useState(false);

    const isLoggedIn = !!user?.token;

    // Keep localStorage in sync
    useEffect(() => {
        if (user) {
            localStorage.setItem("user", JSON.stringify(user));
        } else {
            localStorage.removeItem("user");
            setMessages([]);
            setHasUnacknowledgedMessages(false);
        }
    }, [user]);

    // helper for authenticated fetch
    const fetchWithAuth = useCallback(
        async (endpoint, options = {}) => {
            const attemptFetch = async (token) => {
                const headers = {
                    ...options.headers,
                };

                // Only add Authorization header if token exists
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }

                // Don't override Content-Type if it's already set (e.g., for FormData)
                if (!headers['Content-Type'] && !options.body instanceof FormData) {
                    headers['Content-Type'] = 'application/json';
                }

                const res = await fetch(`${API_BASE_URL}${endpoint}`, {
                    ...options,
                    headers,
                });
                return res;
            };

            let res = await attemptFetch(user?.token);

            if (res.status === 401 && user?.token) {
                // try refresh
                const refreshedToken = await refreshAccessToken();
                if (refreshedToken) {
                    // update user in context
                    setUser((prev) => ({...prev, token: refreshedToken}));
                    // retry original request
                    res = await attemptFetch(refreshedToken);
                } else {
                    // refresh failed → logout
                    await logout();
                    throw new Error("Session expired. Please log in again.");
                }
            }

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || `API error: ${res.status}`);
            }

            return res.json();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.token]
    );

    // helper to refresh access token
    const refreshAccessToken = async () => {

        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) return null;

        try {
            const res = await fetch(`${API_BASE_URL}/refresh`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({refresh_token: refreshToken}),
            });

            if (!res.ok) return null;

            const data = await res.json();
            localStorage.setItem("accessToken", data.token);

            return data.token;
        } catch (err) {
            console.error("Failed to refresh access token", err);
            return null;
        }
    };

    const fetchMessages = useCallback(async () => {

        if (!user?.token) return;
        try {
            const data = await fetchWithAuth("/api/messages/pending");
            if (Array.isArray(data) && data.length > 0) {
                setMessages(data);
                setHasUnacknowledgedMessages(true);
            } else {
                setMessages([]);
                setHasUnacknowledgedMessages(false);
            }

        } catch (err) {
            console.error("Failed to fetch messages:", err);
            setMessages([]);
            setHasUnacknowledgedMessages(false);
        }
    }, [fetchWithAuth, user?.token]);

    // fetch messages automatically whenever user logs in
    useEffect(() => {
        if (user?.token) {
            fetchMessages();
        }
    }, [user?.token, fetchMessages]);


    const login = async (userData) => {

        // Expecting userData to include token + refreshToken from backend
        const {token, refreshToken, ...rest} = userData;

        const newUser = {
            ...rest,
            token,
            refreshToken,
        };

        setUser(newUser);

        // Persist both tokens separately for convenience
        localStorage.setItem("user", JSON.stringify(newUser));
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("accessToken", token);
    };

    const logout = async () => {
        try {
            if (user?.token) {
                await fetch(`${API_BASE_URL}/api/logout`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                });
            }
        } catch (err) {
            console.error("Logout API call failed", err);
        } finally {
            // Clear client-side state
            setUser(null);
            setMessages([]);
            setHasUnacknowledgedMessages(false);
            localStorage.removeItem("user");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("accessToken");
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoggedIn,
                login,
                logout,
                fetchWithAuth,
                messages,
                hasUnacknowledgedMessages,
                fetchMessages,
                setHasUnacknowledgedMessages,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
